import {
  Box,
  Card,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import {
  type ComponentType,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useKey } from 'react-use';

import { Divider } from '@/components/Divider';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { type IconProps } from '@/components/icons/PlusIcon';
import { useOverlayContainer } from '@/components/poppers/OverlayLayer';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

/** The rail is one tool button wide: a 56px button and 4px of padding on each side. */
const RAIL_WIDTH = 65;
/**
 * A flyout opens after this hover delay, so that a pointer that crosses the
 * rail does not open each flyout on its path.
 */
const FLYOUT_OPEN_DELAY_MS = 90;
/**
 * A flyout closes after this delay, so that the pointer can cross the gap
 * between the tool button and the flyout.
 */
const FLYOUT_CLOSE_DELAY_MS = 150;
/** The gap between the rail and an open flyout, in pixels. */
const FLYOUT_GAP = 8;

/**
 * A vertical rail of canvas tools, at the left edge of a canvas. Put
 * `DiagramToolRailItem` and `DiagramToolRailDivider` elements in it.
 */
export function DiagramToolRail({
  children,
  'aria-label': ariaLabel = 'Canvas tools',
}: PropsWithChildren<{ 'aria-label'?: string }>) {
  return (
    <Stack
      component="nav"
      aria-label={ariaLabel}
      gap={0.5}
      p={0.5}
      alignItems="center"
      sx={{
        width: RAIL_WIDTH,
        height: '100%',
        flexShrink: 0,
        backgroundColor: COLORS.PRIMITIVES.WHITE,
        borderRight: `1px solid ${COLORS.GRAY[200]}`,
      }}
    >
      {children}
    </Stack>
  );
}

/** A line between two groups of tools. */
export function DiagramToolRailDivider() {
  return <Divider noMargin sx={{ width: 24, borderColor: COLORS.GRAY[200] }} />;
}

export interface DiagramToolRailItemProps {
  label: string;
  icon: ComponentType<IconProps>;
  /** True when this tool is the current tool. */
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /**
   * The options of the tool, as a `DiagramToolRailMenu`.
   */
  menu?: ReactNode;
  /** A `DiagramToolPreviewCard` that shows what the tool adds. */
  preview?: ReactNode;
}

/**
 * A tool button with an icon above its label. The menu and the preview of
 * the tool open in a flyout to its right when the pointer rests on the tool,
 * or when the user presses the right arrow key on it. A click calls
 * `onClick` and closes the flyout.
 */
export function DiagramToolRailItem({
  label,
  icon: Icon,
  isActive = false,
  disabled = false,
  onClick,
  menu,
  preview,
}: DiagramToolRailItemProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const container = useOverlayContainer();
  const { isOpen, open, close, scheduleOpen, scheduleClose, cancel } =
    useFlyoutState();
  const hasFlyout = (!!menu || !!preview) && !disabled;
  const hasMenu = !!menu && !disabled;
  // A hover opens the flyout without focus in it, so the flyout's own key
  // handler does not get Escape. Listen on the window instead.
  useKey('Escape', close);

  return (
    <Box
      onPointerEnter={(event) => {
        if (hasFlyout && event.pointerType === 'mouse') scheduleOpen();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') scheduleClose();
      }}
      sx={{ position: 'relative' }}
    >
      <IconButton
        ref={anchorRef}
        icon={Icon}
        ariaLabel={label}
        label={label}
        variant="transparent"
        size="xs"
        disabled={disabled}
        aria-pressed={isActive}
        aria-haspopup={hasMenu ? 'menu' : undefined}
        aria-expanded={hasMenu ? isOpen : undefined}
        onClick={() => {
          close();
          onClick?.();
        }}
        onKeyDown={(event) => {
          if (!hasFlyout) return;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            open();
          }
          if (event.key === 'Escape') close();
        }}
        sx={{
          width: 56,
          padding: '8px 4px',
          borderRadius: '2px',
          ...((isActive || isOpen) && { backgroundColor: COLORS.GRAY[100] }),
        }}
      />
      {hasFlyout && (
        <Popper
          container={container}
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="right-start"
          modifiers={[{ name: 'offset', options: { offset: [0, FLYOUT_GAP] } }]}
          sx={{ zIndex: zIndexes.POPOVER }}
          onPointerEnter={cancel}
          onPointerLeave={scheduleClose}
          onKeyDown={(event) => {
            if (event.key === 'Escape' || event.key === 'ArrowLeft') {
              event.preventDefault();
              close();
              anchorRef.current?.focus();
            }
          }}
          // A click on a flyout option selects it; close the flyout after it.
          onClick={close}
        >
          <Stack direction="row" gap={1} alignItems="flex-start">
            {menu}
            {preview}
          </Stack>
        </Popper>
      )}
    </Box>
  );
}

/** The open state of a hover flyout, with the open and close delays. */
function useFlyoutState() {
  const [isOpen, setIsOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const cancel = () => clearTimeout(timer.current);
  useEffect(() => cancel, []);

  return {
    isOpen,
    cancel,
    open: () => {
      cancel();
      setIsOpen(true);
    },
    close: () => {
      cancel();
      setIsOpen(false);
    },
    scheduleOpen: () => {
      cancel();
      timer.current = setTimeout(() => setIsOpen(true), FLYOUT_OPEN_DELAY_MS);
    },
    scheduleClose: () => {
      cancel();
      timer.current = setTimeout(() => setIsOpen(false), FLYOUT_CLOSE_DELAY_MS);
    },
  };
}

/**
 * A list of tool options in a flyout. Put `MenuItem` elements from
 * `@/components/poppers/MenuPopper/MenuItem` in it.
 */
export function DiagramToolRailMenu({
  children,
  'aria-label': ariaLabel,
}: PropsWithChildren<{ 'aria-label': string }>) {
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        minWidth: 168,
        border: `1px solid ${COLORS.GRAY[200]}`,
        boxShadow: theme.palette.shadows.lg,
      })}
    >
      <MenuList aria-label={ariaLabel} autoFocusItem={false}>
        {children}
      </MenuList>
    </Paper>
  );
}

export interface DiagramToolPreviewCardProps {
  /**
   * A drawing of the element that the tool adds, 164 pixels wide. The
   * drawing supplies its own background.
   */
  art: ReactNode;
  description: ReactNode;
}

/** A card that shows what a tool adds to the canvas. */
export function DiagramToolPreviewCard({
  art,
  description,
}: DiagramToolPreviewCardProps) {
  return (
    <Card
      variant="outlined"
      aria-hidden
      sx={(theme) => ({
        width: 347,
        p: 2,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        borderRadius: '6px',
        boxShadow: theme.palette.shadows.xs,
      })}
    >
      <Box sx={{ width: 164, flexShrink: 0 }}>{art}</Box>
      <Stack gap={0.25}>
        <Typography variant="eyebrow-xs" component="span">
          Preview
        </Typography>
        <Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>
          {description}
        </Typography>
      </Stack>
    </Card>
  );
}
